/**
 * Rug Warper Engine - Native High-Performance WebGL & OpenCV.js Parity Engine
 * 100% Parity Implementation of assets/rug_warper.py
 * Features:
 * - 2x SSAA Supersampling Anti-Aliasing & Scale-Aware Moiré Pre-Blur
 * - Realistic PolyFill Contact & Ambient Floor Shadows (createShadow)
 * - True Floating Alpha Edge Feathering (soft_edges) & Perimeter Moiré Edge Blend
 * - OpenCV Convex Hull & Moments for Visible Hull Centroid
 * - Strict Memory Management & Garbage Collection (.delete() on all OpenCV Mats & WebGL textures)
 * - Persistent Anchor Tracking (get_persistent_anchor_js / set_persistent_anchor_js)
 */

(function (window) {
  'use strict';

  // --- REJECT REASONS ---
  const RejectReason = {
    ABOVE_HORIZON_UI: "ABOVE_HORIZON_UI",
    NEAR_HORIZON_RAY: "NEAR_HORIZON_RAY",
    INVALID_DISTANCE: "INVALID_DISTANCE",
    BEHIND_CAMERA: "BEHIND_CAMERA",
    CORNER_ABOVE_HORIZON: "CORNER_ABOVE_HORIZON",
    LOW_FLOOR_OVERLAP: "LOW_FLOOR_OVERLAP",
  };

  // --- HELPER MATH ---
  function norm3(v) {
    const len = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]);
    return len < 1e-9 ? 1.0 : len;
  }

  function normalize3(v) {
    const len = norm3(v);
    return [v[0] / len, v[1] / len, v[2] / len];
  }

  function dot3(a, b) {
    return a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
  }

  function cross3(a, b) {
    return [
      a[1] * b[2] - a[2] * b[1],
      a[2] * b[0] - a[0] * b[2],
      a[0] * b[1] - a[1] * b[0]
    ];
  }

  function mat3Invert(m) {
    const a00 = m[0], a01 = m[1], a02 = m[2];
    const a10 = m[3], a11 = m[4], a12 = m[5];
    const a20 = m[6], a21 = m[7], a22 = m[8];

    const b01 = a22 * a11 - a12 * a21;
    const b11 = -a22 * a10 + a12 * a20;
    const b21 = a21 * a10 - a11 * a20;

    const det = a00 * b01 + a01 * b11 + a02 * b21;
    if (Math.abs(det) < 1e-9) return null;
    const invDet = 1.0 / det;

    return [
      b01 * invDet, (-a22 * a01 + a02 * a21) * invDet, (a12 * a01 - a02 * a11) * invDet,
      b11 * invDet, (a22 * a00 - a02 * a20) * invDet, (-a12 * a00 + a02 * a10) * invDet,
      b21 * invDet, (-a21 * a00 + a01 * a20) * invDet, (a11 * a00 - a01 * a10) * invDet
    ];
  }

  function mat3Vec3Multiply(m, v) {
    return [
      m[0] * v[0] + m[1] * v[1] + m[2] * v[2],
      m[3] * v[0] + m[4] * v[1] + m[5] * v[2],
      m[6] * v[0] + m[7] * v[1] + m[8] * v[2]
    ];
  }

  function computeHomography(src, dst) {
    const A = [];
    const b = [];
    for (let i = 0; i < 4; i++) {
      const x = src[i][0], y = src[i][1];
      const u = dst[i][0], v = dst[i][1];
      A.push([x, y, 1, 0, 0, 0, -u * x, -u * y]);
      b.push(u);
      A.push([0, 0, 0, x, y, 1, -v * x, -v * y]);
      b.push(v);
    }

    for (let i = 0; i < 8; i++) {
      let maxRow = i;
      for (let k = i + 1; k < 8; k++) {
        if (Math.abs(A[k][i]) > Math.abs(A[maxRow][i])) maxRow = k;
      }
      const tmpRow = A[i]; A[i] = A[maxRow]; A[maxRow] = tmpRow;
      const tmpB = b[i]; b[i] = b[maxRow]; b[maxRow] = tmpB;

      if (Math.abs(A[i][i]) < 1e-9) return null;

      for (let k = i + 1; k < 8; k++) {
        const c = A[k][i] / A[i][i];
        for (let j = i; j < 8; j++) A[k][j] -= c * A[i][j];
        b[k] -= c * b[i];
      }
    }

    const h = new Array(9);
    h[8] = 1.0;
    for (let i = 7; i >= 0; i--) {
      let s = b[i];
      for (let j = i + 1; j < 8; j++) s -= A[i][j] * h[j];
      h[i] = s / A[i][i];
    }

    return mat3Invert(h);
  }

  function moirePreBlur(rugMat, srcQuad, dstQuad) {
    if (typeof cv === 'undefined' || typeof cv.GaussianBlur !== 'function') return rugMat;
    try {
      function quadAvgSide(q) {
        const d0 = Math.hypot(q[1][0] - q[0][0], q[1][1] - q[0][1]);
        const d1 = Math.hypot(q[2][0] - q[1][0], q[2][1] - q[1][1]);
        const d2 = Math.hypot(q[3][0] - q[2][0], q[3][1] - q[2][1]);
        const d3 = Math.hypot(q[0][0] - q[3][0], q[0][1] - q[3][1]);
        return (d0 + d1 + d2 + d3) / 4.0;
      }
      const srcSize = quadAvgSide(srcQuad);
      const dstSize = quadAvgSide(dstQuad);

      if (srcSize < 1.0) return rugMat;
      const scale = dstSize / Math.max(srcSize, 1.0);
      if (scale >= 0.8) return rugMat;

      // Cap max sigma to 0.7 so fine rug texture details remain crisp
      const sigma = Math.max(0.3, Math.min(0.7, 0.3 / Math.max(scale, 0.1)));
      const k = Math.max(3, (Math.ceil(sigma * 2) | 1));

      const blurred = new cv.Mat();
      cv.GaussianBlur(rugMat, blurred, new cv.Size(k, k), sigma, sigma);
      return blurred;
    } catch (e) {
      return rugMat;
    }
  }

  function createShadow(pts, h, w, floorMaskMat, rugMaskMat, hq = true) {
    if (typeof cv === 'undefined' || !cv.Mat) return null;
    try {
      const shadow = cv.Mat.zeros(h, w, cv.CV_32FC1);

      // Contact shadow polygon (Y + 4)
      const contactPtsData = new Int32Array(8);
      for (let i = 0; i < 4; i++) {
        contactPtsData[i * 2] = Math.round(pts[i][0]);
        contactPtsData[i * 2 + 1] = Math.round(pts[i][1] + 4);
      }
      const contactVector = new cv.MatVector();
      contactVector.push_back(cv.matFromArray(4, 1, cv.CV_32SC2, contactPtsData));
      cv.fillPoly(shadow, contactVector, new cv.Scalar(0.6));
      contactVector.get(0).delete(); contactVector.delete();

      // Ambient shadow polygon (Y + 15)
      const ambientShadow = cv.Mat.zeros(h, w, cv.CV_32FC1);
      const ambientPtsData = new Int32Array(8);
      for (let i = 0; i < 4; i++) {
        ambientPtsData[i * 2] = Math.round(pts[i][0]);
        ambientPtsData[i * 2 + 1] = Math.round(pts[i][1] + 15);
      }
      const ambientVector = new cv.MatVector();
      ambientVector.push_back(cv.matFromArray(4, 1, cv.CV_32SC2, ambientPtsData));
      cv.fillPoly(ambientShadow, ambientVector, new cv.Scalar(0.3));
      ambientVector.get(0).delete(); ambientVector.delete();

      cv.max(shadow, ambientShadow, shadow);
      ambientShadow.delete();

      const k = hq ? 45 : 15;
      cv.GaussianBlur(shadow, shadow, new cv.Size(k, k), 0, 0);

      // Multiply floor mask
      const floorMaskFloat = new cv.Mat();
      floorMaskMat.convertTo(floorMaskFloat, cv.CV_32FC1, 1.0 / 255.0);
      cv.multiply(shadow, floorMaskFloat, shadow);
      floorMaskFloat.delete();

      // Multiply rug mask
      if (rugMaskMat) {
        const rugMaskFloat = new cv.Mat();
        rugMaskMat.convertTo(rugMaskFloat, cv.CV_32FC1, 1.0 / 255.0);
        cv.multiply(shadow, rugMaskFloat, shadow);
        rugMaskFloat.delete();
      }

      shadow.convertTo(shadow, -1, 0.40, 0);
      return shadow;
    } catch (e) {
      return null;
    }
  }

  function moireEdgeBlend(finalMat, warpedMaskMat, roomMat, strength = 0.09) {
    if (typeof cv === 'undefined' || !cv.Mat || !warpedMaskMat || warpedMaskMat.empty()) return finalMat;
    try {
      const k7 = cv.Mat.ones(7, 7, cv.CV_8UC1);
      const k3 = cv.Mat.ones(3, 3, cv.CV_8UC1);
      const dilated = new cv.Mat();
      const eroded = new cv.Mat();
      const edgeBand = new cv.Mat();

      cv.dilate(warpedMaskMat, dilated, k7);
      cv.erode(warpedMaskMat, eroded, k3);
      cv.subtract(dilated, eroded, edgeBand);

      const edgeFloat = new cv.Mat();
      edgeBand.convertTo(edgeFloat, cv.CV_32FC1, 1.0 / 255.0);
      cv.GaussianBlur(edgeFloat, edgeFloat, new cv.Size(11, 11), 0, 0);
      edgeFloat.convertTo(edgeFloat, -1, strength, 0);

      const edge4 = new cv.Mat();
      const vec = new cv.MatVector();
      vec.push_back(edgeFloat); vec.push_back(edgeFloat); vec.push_back(edgeFloat); vec.push_back(edgeFloat);
      cv.merge(vec, edge4);

      const roomFloat = new cv.Mat();
      roomMat.convertTo(roomFloat, cv.CV_32FC4, 1.0 / 255.0);

      const finalFloat = new cv.Mat();
      finalMat.convertTo(finalFloat, cv.CV_32FC4, 1.0 / 255.0);

      const oneMinusEdge = new cv.Mat();
      const onesEdge = new cv.Mat(finalMat.rows, finalMat.cols, cv.CV_32FC4, new cv.Scalar(1.0, 1.0, 1.0, 1.0));
      cv.subtract(onesEdge, edge4, oneMinusEdge);
      onesEdge.delete();

      const rugPart = new cv.Mat();
      cv.multiply(finalFloat, oneMinusEdge, rugPart);

      const roomPart = new cv.Mat();
      cv.multiply(roomFloat, edge4, roomPart);

      const blendedFloat = new cv.Mat();
      cv.add(rugPart, roomPart, blendedFloat);

      const result = new cv.Mat();
      blendedFloat.convertTo(result, cv.CV_8UC4, 255.0);

      k7.delete(); k3.delete(); dilated.delete(); eroded.delete(); edgeBand.delete();
      edgeFloat.delete(); edge4.delete(); vec.get(0).delete(); vec.delete();
      roomFloat.delete(); finalFloat.delete(); oneMinusEdge.delete(); rugPart.delete(); roomPart.delete(); blendedFloat.delete();

      return result;
    } catch (e) {
      return finalMat;
    }
  }


  // --- RUG CROP & MASK EXTRACTOR ---
  function extractRugCropAndMask(rugImgData) {
    if (!rugImgData || !rugImgData.width || !rugImgData.height || !rugImgData.data) {
      const fb = document.createElement('canvas');
      fb.width = 100; fb.height = 100;
      return { croppedCanvas: fb, cropW: 100, cropH: 100, cropMask: new Uint8Array(10000).fill(255) };
    }

    const w = rugImgData.width;
    const h = rugImgData.height;
    const data = rugImgData.data;

    let minX = w, minY = h, maxX = 0, maxY = 0;
    let hasAlpha = false;

    for (let i = 3; i < data.length; i += 4) {
      if (data[i] < 200) {
        hasAlpha = true;
        break;
      }
    }

    const mask = new Uint8Array(w * h);

    if (hasAlpha) {
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const idx = (y * w + x) * 4;
          if (data[idx + 3] > 20) {
            mask[y * w + x] = 255;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
    } else {
      for (let y = 0; y < h; y++) {
        for (let x = 0; x < w; x++) {
          const idx = (y * w + x) * 4;
          const r = data[idx], g = data[idx + 1], b = data[idx + 2];
          const isWhite = (r > 240 && g > 240 && b > 240 && Math.abs(r - g) < 15 && Math.abs(r - b) < 15);
          if (!isWhite) {
            mask[y * w + x] = 255;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
    }

    if (minX > maxX || minY > maxY) {
      minX = 0; minY = 0; maxX = w - 1; maxY = h - 1;
      mask.fill(255);
    }

    const cropW = Math.max(1, maxX - minX + 1);
    const cropH = Math.max(1, maxY - minY + 1);

    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = cropW;
    croppedCanvas.height = cropH;
    const ctx = croppedCanvas.getContext('2d');

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = w; tempCanvas.height = h;
    tempCanvas.getContext('2d').putImageData(rugImgData, 0, 0);

    ctx.drawImage(tempCanvas, minX, minY, cropW, cropH, 0, 0, cropW, cropH);

    const cropMask = new Uint8Array(cropW * cropH);
    for (let y = 0; y < cropH; y++) {
      for (let x = 0; x < cropW; x++) {
        cropMask[y * cropW + x] = mask[(minY + y) * w + (minX + x)];
      }
    }

    // Smooth alpha matte along silhouette edges (2-3px feathering)
    try {
      const croppedCtx = croppedCanvas.getContext('2d');
      const croppedImgData = croppedCtx.getImageData(0, 0, cropW, cropH);
      const cData = croppedImgData.data;

      const tempAlpha = new Float32Array(cropW * cropH);
      for (let i = 0; i < cropW * cropH; i++) {
        tempAlpha[i] = cropMask[i] > 127 ? 1.0 : 0.0;
      }

      const smoothedAlpha = new Float32Array(cropW * cropH);
      const radius = 1;
      for (let pass = 0; pass < 2; pass++) {
        const srcA = pass === 0 ? tempAlpha : smoothedAlpha;
        for (let cy = 0; cy < cropH; cy++) {
          for (let cx = 0; cx < cropW; cx++) {
            let sum = 0.0;
            let count = 0;
            for (let dy = -radius; dy <= radius; dy++) {
              const ny = cy + dy;
              if (ny >= 0 && ny < cropH) {
                for (let dx = -radius; dx <= radius; dx++) {
                  const nx = cx + dx;
                  if (nx >= 0 && nx < cropW) {
                    sum += srcA[ny * cropW + nx];
                    count++;
                  }
                }
              }
            }
            smoothedAlpha[cy * cropW + cx] = sum / Math.max(1, count);
          }
        }
      }

      for (let i = 0; i < cropW * cropH; i++) {
        cData[i * 4 + 3] = Math.round(smoothedAlpha[i] * cData[i * 4 + 3]);
      }
      croppedCtx.putImageData(croppedImgData, 0, 0);
    } catch (_) {}

    return { croppedCanvas, cropW, cropH, cropMask };
  }

  function nextPowerOfTwo(n) {
    return Math.pow(2, Math.ceil(Math.log2(Math.max(1, n))));
  }

  function makePowerOfTwoCanvas(srcCanvas) {
    const potW = nextPowerOfTwo(srcCanvas.width);
    const potH = nextPowerOfTwo(srcCanvas.height);
    const potCanvas = document.createElement('canvas');
    potCanvas.width = potW;
    potCanvas.height = potH;
    const ctx = potCanvas.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(srcCanvas, 0, 0, potW, potH);
    return potCanvas;
  }

  // --- GEOMETRY ENGINE ---
  class GeometryEngine {
    constructor(K_list, plane_coeffs, img_w, img_h) {
      const kArr = Array.isArray(K_list) ? K_list.flat() : [];
      this.K = [
        kArr[0] || 2000, kArr[1] || 0, kArr[2] || (img_w / 2),
        kArr[3] || 0, kArr[4] || 2000, kArr[5] || (img_h / 2),
        kArr[6] || 0, kArr[7] || 0, kArr[8] || 1
      ];
      this.img_w = parseFloat(img_w);
      this.img_h = parseFloat(img_h);

      this.horizon_y = null;
      const kInv = mat3Invert(this.K);
      if (kInv) {
        const n = normalize3([0.0, -0.96, -0.28]);
        const cx = this.K[2];
        for (let y = 0; y < this.img_h; y += 5) {
          const pixel = [cx, parseFloat(y), 1.0];
          const ray = normalize3(mat3Vec3Multiply(kInv, pixel));
          const denom = dot3(n, ray);
          if (denom < -0.05) {
            this.horizon_y = y;
            break;
          }
        }
      }
      if (this.horizon_y === null) {
        this.horizon_y = Math.floor(this.K[5]);
      }
    }

    get_projected_corners(cx_px, cy_px, w_m, h_m, rot_deg) {
      const kInv = mat3Invert(this.K);
      if (!kInv) return [null, RejectReason.INVALID_DISTANCE];

      const n = normalize3([0.0, -0.96, -0.28]);
      const camHeight = 1.55;

      const pixel = [cx_px, cy_px, 1.0];
      const ray = normalize3(mat3Vec3Multiply(kInv, pixel));

      let denom = dot3(n, ray);
      if (denom > -0.01) denom = -0.01;

      let tCenter = -camHeight / denom;
      if (tCenter > 50.0) tCenter = 50.0;
      if (tCenter <= 0.0) return [null, RejectReason.INVALID_DISTANCE];

      const center3d = [tCenter * ray[0], tCenter * ray[1], tCenter * ray[2]];

      let worldUp = [0.0, 1.0, 0.0];
      if (Math.abs(dot3(n, worldUp)) > 0.95) worldUp = [0.0, 0.0, 1.0];

      const u = normalize3(cross3(n, worldUp));
      const v = cross3(n, u);

      const rad = (rot_deg * Math.PI) / 180.0;
      const cosR = Math.cos(rad);
      const sinR = Math.sin(rad);

      const uRot = [u[0] * cosR + v[0] * sinR, u[1] * cosR + v[1] * sinR, u[2] * cosR + v[2] * sinR];
      const vRot = [-u[0] * sinR + v[0] * cosR, -u[1] * sinR + v[1] * cosR, -u[2] * sinR + v[2] * cosR];

      const w2 = w_m / 2.0;
      const h2 = h_m / 2.0;

      const corners3d = [
        [center3d[0] - uRot[0]*w2 - vRot[0]*h2, center3d[1] - uRot[1]*w2 - vRot[1]*h2, center3d[2] - uRot[2]*w2 - vRot[2]*h2],
        [center3d[0] + uRot[0]*w2 - vRot[0]*h2, center3d[1] + uRot[1]*w2 - vRot[1]*h2, center3d[2] + uRot[2]*w2 - vRot[2]*h2],
        [center3d[0] + uRot[0]*w2 + vRot[0]*h2, center3d[1] + uRot[1]*w2 + vRot[1]*h2, center3d[2] + uRot[2]*w2 + vRot[2]*h2],
        [center3d[0] - uRot[0]*w2 + vRot[0]*h2, center3d[1] - uRot[1]*w2 + vRot[1]*h2, center3d[2] - uRot[2]*w2 + vRot[2]*h2],
      ];

      const corners2d = [];
      for (let i = 0; i < 4; i++) {
        const proj = mat3Vec3Multiply(this.K, corners3d[i]);
        let z = proj[2];
        if (z <= 0.001) z = 0.001;
        corners2d.push([proj[0] / z, proj[1] / z]);
      }
      return [corners2d, null];
    }
  }

  function getImageDataFromInput(input) {
    if (!input) return null;
    if (input.data && input.width && input.height) return input;
    if (typeof HTMLCanvasElement !== 'undefined' && input instanceof HTMLCanvasElement) {
      const ctx = input.getContext('2d', { willReadFrequently: true });
      return ctx.getImageData(0, 0, input.width, input.height);
    }
    if (input.getContext && input.width && input.height) {
      const ctx = input.getContext('2d', { willReadFrequently: true });
      return ctx.getImageData(0, 0, input.width, input.height);
    }
    return null;
  }

  // --- RUG WARPER ENGINE MAIN CLASS ---
  class RugWarperEngine {
    constructor(roomInput, rugInput, floorMaskInput, K, P) {
      const roomImgData = getImageDataFromInput(roomInput);
      const rugImgData = getImageDataFromInput(rugInput);
      const floorMaskImgData = getImageDataFromInput(floorMaskInput);

      if (!roomImgData || !rugImgData || !floorMaskImgData) {
        throw new Error('❌ RugWarperEngine: Invalid or unreadable inputs');
      }

      this.w = roomImgData.width;
      this.h = roomImgData.height;

      // Base Room Canvas
      this.roomCanvas = document.createElement('canvas');
      this.roomCanvas.width = this.w;
      this.roomCanvas.height = this.h;
      this.roomCtx = this.roomCanvas.getContext('2d');
      this.roomCtx.putImageData(roomImgData, 0, 0);

      // Floor mask bitmap - 1-to-1 grayscale conversion matching cv2.cvtColor(RGBA, COLOR_RGBA2GRAY)
      this.floorMaskData = floorMaskImgData.data;
      this.floorMaskU8 = new Uint8Array(this.w * this.h);
      let totalFloorPx = 0;
      for (let i = 0; i < this.w * this.h; i++) {
        const r = this.floorMaskData[i * 4];
        const g = this.floorMaskData[i * 4 + 1];
        const b = this.floorMaskData[i * 4 + 2];
        const gray = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
        this.floorMaskU8[i] = gray;
        if (gray > 127) totalFloorPx++;
      }
      this._total_floor_px = Math.max(1, totalFloorPx);

      // Pre-smooth floorMaskU8 (5x5 separable Gaussian filter) to remove stair-step jaggies along chair legs
      try {
        const rawMask = new Float32Array(this.floorMaskU8);
        const radius = 2; // 5x5 kernel
        const weights = [1, 4, 6, 4, 1];
        const weightSum = 16;

        const tempBuf = new Float32Array(this.w * this.h);
        for (let y = 0; y < this.h; y++) {
          const yOff = y * this.w;
          for (let x = 0; x < this.w; x++) {
            let sum = 0;
            for (let dx = -radius; dx <= radius; dx++) {
              const nx = Math.min(this.w - 1, Math.max(0, x + dx));
              sum += rawMask[yOff + nx] * weights[dx + radius];
            }
            tempBuf[yOff + x] = sum / weightSum;
          }
        }

        const smoothMask = new Float32Array(this.w * this.h);
        for (let y = 0; y < this.h; y++) {
          for (let x = 0; x < this.w; x++) {
            let sum = 0;
            for (let dy = -radius; dy <= radius; dy++) {
              const ny = Math.min(this.h - 1, Math.max(0, y + dy));
              sum += tempBuf[ny * this.w + x] * weights[dy + radius];
            }
            smoothMask[y * this.w + x] = sum / weightSum;
          }
        }

        for (let i = 0; i < this.w * this.h; i++) {
          this.floorMaskU8[i] = Math.round(smoothMask[i]);
        }
      } catch (_) {}

      // Process Rug crop
      const rugRes = extractRugCropAndMask(rugImgData);
      this.rugCanvas = rugRes.croppedCanvas;
      this.w_rug = rugRes.cropW;
      this.h_rug = rugRes.cropH;
      this.rugMaskSrc = rugRes.cropMask;

      this.srcQuad = [
        [0, 0],
        [this.w_rug, 0],
        [this.w_rug, this.h_rug],
        [0, this.h_rug]
      ];

      // Geometry engine
      this.geo = new GeometryEngine(K, P, this.w, this.h);

      this.last_reject = null;
      this.last_visible_ratio = 0.0;
      this.last_hull_cx = -1.0;
      this.last_hull_cy = -1.0;

      this._persistent_anchor_cx = null;
      this._persistent_anchor_cy = null;

      // Reusable polygon canvas for overlap checks
      this.polyCanvas = document.createElement('canvas');
      this.polyCanvas.width = this.w;
      this.polyCanvas.height = this.h;

      // Helper 2D Canvas
      this.helper2DCanvas = document.createElement('canvas');
      this.helper2DCanvas.width = this.w;
      this.helper2DCanvas.height = this.h;
      this.helper2DCtx = this.helper2DCanvas.getContext('2d', { willReadFrequently: true });

      // Offscreen WebGL canvas
      this.offscreenCanvas = document.createElement('canvas');
      this.offscreenCanvas.width = this.w;
      this.offscreenCanvas.height = this.h;

      this.initWebGL();
    }

    initWebGL() {
      try {
        const gl = this.offscreenCanvas.getContext('webgl2', { preserveDrawingBuffer: true, alpha: true }) ||
                   this.offscreenCanvas.getContext('webgl', { preserveDrawingBuffer: true, alpha: true }) ||
                   this.offscreenCanvas.getContext('experimental-webgl');
        if (!gl) {
          console.warn('WebGL context unavailable, using 2D Canvas engine');
          return;
        }
        this.gl = gl;

        const vsSource = `
          attribute vec2 a_position;
          varying vec2 v_texCoord;
          void main() {
            v_texCoord = a_position;
            vec2 clipSpace = a_position * 2.0 - 1.0;
            gl_Position = vec4(clipSpace.x, -clipSpace.y, 0.0, 1.0);
          }
        `;

        const fsSource = `
          precision mediump float;
          varying vec2 v_texCoord;

          uniform sampler2D u_roomTexture;
          uniform sampler2D u_rugTexture;
          uniform sampler2D u_floorMaskTexture;
          uniform mat3 u_H_inv;
          uniform vec2 u_resolution;
          uniform vec2 u_rugSize;

          void main() {
            vec2 canvasPx = v_texCoord * u_resolution;
            vec4 roomColor = texture2D(u_roomTexture, v_texCoord);
            float floorVal = texture2D(u_floorMaskTexture, v_texCoord).r;

            vec3 rugH = u_H_inv * vec3(canvasPx, 1.0);
            if (rugH.z > 0.0 && floorVal > 0.01) {
              vec2 rugPx = rugH.xy / rugH.z;
              if (rugPx.x >= -2.0 && rugPx.x <= u_rugSize.x + 2.0 && rugPx.y >= -2.0 && rugPx.y <= u_rugSize.y + 2.0) {
                vec2 rugUV = clamp(rugPx / u_rugSize, 0.0, 1.0);
                vec4 rugColor = texture2D(u_rugTexture, rugUV);

                // Smooth feathering along quad boundaries (3px margin)
                float edgePxX = min(rugPx.x, u_rugSize.x - rugPx.x);
                float edgePxY = min(rugPx.y, u_rugSize.y - rugPx.y);
                float quadAlpha = smoothstep(-1.0, 3.0, edgePxX) * smoothstep(-1.0, 3.0, edgePxY);

                // Smooth floor mask transition (calibrated S-curve for anti-aliasing chair legs and furniture)
                float floorAlpha = smoothstep(0.12, 0.88, floorVal);

                float finalAlpha = rugColor.a * quadAlpha * floorAlpha;
                if (finalAlpha > 0.001) {
                  gl_FragColor = mix(roomColor, rugColor, finalAlpha);
                  return;
                }
              }
            }
            gl_FragColor = roomColor;
          }
        `;

        function createShader(gl, type, source) {
          const shader = gl.createShader(type);
          gl.shaderSource(shader, source);
          gl.compileShader(shader);
          if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
            console.error('Shader compile error:', gl.getShaderInfoLog(shader));
            gl.deleteShader(shader);
            return null;
          }
          return shader;
        }

        const vs = createShader(gl, gl.VERTEX_SHADER, vsSource);
        const fs = createShader(gl, gl.FRAGMENT_SHADER, fsSource);
        if (!vs || !fs) return;

        const program = gl.createProgram();
        gl.attachShader(program, vs);
        gl.attachShader(program, fs);
        gl.linkProgram(program);
        gl.useProgram(program);
        this.glProgram = program;

        const positionBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([
          0, 0,  1, 0,  0, 1,
          0, 1,  1, 0,  1, 1
        ]), gl.STATIC_DRAW);

        const aPosition = gl.getAttribLocation(program, 'a_position');
        gl.enableVertexAttribArray(aPosition);
        gl.vertexAttribPointer(aPosition, 2, gl.FLOAT, false, 0, 0);

        this.texRoom = this.createGLTexture(gl, this.roomCanvas);
        this.texRug = this.createGLTexture(gl, this.rugCanvas, false);

        const maskCanvas = document.createElement('canvas');
        maskCanvas.width = this.w; maskCanvas.height = this.h;
        const maskCtx = maskCanvas.getContext('2d');
        const maskImgData = maskCtx.createImageData(this.w, this.h);
        for (let i = 0; i < this.w * this.h; i++) {
          const val = this.floorMaskU8[i];
          maskImgData.data[i * 4] = val;
          maskImgData.data[i * 4 + 1] = val;
          maskImgData.data[i * 4 + 2] = val;
          maskImgData.data[i * 4 + 3] = 255;
        }
        maskCtx.putImageData(maskImgData, 0, 0);
        this.texFloor = this.createGLTexture(gl, maskCanvas);
      } catch (e) {
        console.warn('WebGL initialization failed, falling back to 2D Canvas:', e);
        this.gl = null;
      }
    }

    createGLTexture(gl, imageSource, isMipmapped = false) {
      const tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, imageSource);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

      if (isMipmapped) {
        try { gl.generateMipmap(gl.TEXTURE_2D); } catch (_) {}
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);

        // Enable Max Anisotropic Filtering for razor-sharp rug textures at perspective angles
        const ext = gl.getExtension('EXT_texture_filter_anisotropic') || gl.getExtension('WEBKIT_EXT_texture_filter_anisotropic');
        if (ext) {
          const maxAnisotropy = gl.getParameter(ext.MAX_TEXTURE_MAX_ANISOTROPY_EXT) || 8;
          gl.texParameterf(gl.TEXTURE_2D, ext.TEXTURE_MAX_ANISOTROPY_EXT, maxAnisotropy);
        }
      } else {
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      }
      return tex;
    }

    _render_rgba(cx, cy, w_ft, h_ft, rot_deg, hq) {
      this.last_reject = null;
      this.last_visible_ratio = 0.0;

      const w_m = parseFloat(w_ft) * 0.3048;
      const h_m = parseFloat(h_ft) * 0.3048;

      const min_cy = parseInt(this.geo.horizon_y, 10) + 8;
      const cy_clamped = Math.max(parseFloat(cy), parseFloat(min_cy));

      const [pts, reason] = this.geo.get_projected_corners(cx, cy_clamped, w_m, h_m, rot_deg);
      if (!pts) {
        this.last_reject = reason;
        this.helper2DCtx.drawImage(this.roomCanvas, 0, 0);
        return this.helper2DCanvas;
      }

      const dstQuad = pts;
      const H_inv = computeHomography(this.srcQuad, dstQuad);
      if (!H_inv) {
        this.last_reject = RejectReason.INVALID_DISTANCE;
        this.helper2DCtx.drawImage(this.roomCanvas, 0, 0);
        return this.helper2DCanvas;
      }

      // Fast floor mask overlap calculation
      const minX = Math.max(0, Math.floor(Math.min(dstQuad[0][0], dstQuad[1][0], dstQuad[2][0], dstQuad[3][0])));
      const maxX = Math.min(this.w - 1, Math.ceil(Math.max(dstQuad[0][0], dstQuad[1][0], dstQuad[2][0], dstQuad[3][0])));
      const minY = Math.max(0, Math.floor(Math.min(dstQuad[0][1], dstQuad[1][1], dstQuad[2][1], dstQuad[3][1])));
      const maxY = Math.min(this.h - 1, Math.ceil(Math.max(dstQuad[0][1], dstQuad[1][1], dstQuad[2][1], dstQuad[3][1])));

      const pCtx = this.polyCanvas.getContext('2d');
      pCtx.clearRect(0, 0, this.w, this.h);
      pCtx.beginPath();
      pCtx.moveTo(dstQuad[0][0], dstQuad[0][1]);
      pCtx.lineTo(dstQuad[1][0], dstQuad[1][1]);
      pCtx.lineTo(dstQuad[2][0], dstQuad[2][1]);
      pCtx.lineTo(dstQuad[3][0], dstQuad[3][1]);
      pCtx.closePath();

      let floorOverlap = 0;
      let rugArea = 0;
      let sumX = 0, sumY = 0;
      const step = 4;

      for (let y = minY; y <= maxY; y += step) {
        for (let x = minX; x <= maxX; x += step) {
          if (pCtx.isPointInPath(x, y)) {
            rugArea++;
            const idx = y * this.w + x;
            if (this.floorMaskU8[idx] > 127) {
              floorOverlap++;
              sumX += x;
              sumY += y;
            }
          }
        }
      }

      const visible_ratio = floorOverlap / Math.max(1, rugArea);
      this.last_visible_ratio = visible_ratio;

      if (floorOverlap > 0) {
        this.last_hull_cx = sumX / floorOverlap;
        this.last_hull_cy = sumY / floorOverlap;
      } else {
        this.last_hull_cx = -1.0;
        this.last_hull_cy = -1.0;
      }

      const roomPixels = (this.h * this.w) / (step * step);
      const rugFraction = rugArea / Math.max(1, roomPixels);
      const occupiedRatio = floorOverlap / (this._total_floor_px / (step * step));
      const min_visible = rugFraction < 0.005 ? 0.05 : (rugFraction < 0.02 ? 0.08 : 0.05);

      if (visible_ratio < min_visible || (occupiedRatio > 0 && occupiedRatio < 0.05 && visible_ratio < 0.90)) {
        this.last_reject = RejectReason.LOW_FLOOR_OVERLAP;
        this.helper2DCtx.drawImage(this.roomCanvas, 0, 0);
        return this.helper2DCanvas;
      }

      // Track persistent anchor when placement overlap is valid (>= 5%)
      if (visible_ratio >= 0.05) {
        this._persistent_anchor_cx = cx;
        this._persistent_anchor_cy = cy;
      }

      // Check OpenCV.js availability for 1-to-1 SSAA, createShadow, soft_edges feathering, & convexHull
      if (hq && typeof cv !== 'undefined' && typeof cv.Mat === 'function' && cv.imread) {
        try {
          const roomMat = cv.imread(this.roomCanvas);
          const rugMat = cv.imread(this.rugCanvas);

          const srcTri = cv.matFromArray(4, 1, cv.CV_32FC2, [
            this.srcQuad[0][0], this.srcQuad[0][1],
            this.srcQuad[1][0], this.srcQuad[1][1],
            this.srcQuad[2][0], this.srcQuad[2][1],
            this.srcQuad[3][0], this.srcQuad[3][1]
          ]);
          const dstTri = cv.matFromArray(4, 1, cv.CV_32FC2, [
            dstQuad[0][0], dstQuad[0][1],
            dstQuad[1][0], dstQuad[1][1],
            dstQuad[2][0], dstQuad[2][1],
            dstQuad[3][0], dstQuad[3][1]
          ]);

          const ssFactor = 2.0;
          let warpedRug = new cv.Mat();
          let warpedMask = new cv.Mat();
          let rugBgrToWarp = null;

          if (ssFactor > 1.0) {
            const dstTriSS = cv.matFromArray(4, 1, cv.CV_32FC2, [
              dstQuad[0][0] * ssFactor, dstQuad[0][1] * ssFactor,
              dstQuad[1][0] * ssFactor, dstQuad[1][1] * ssFactor,
              dstQuad[2][0] * ssFactor, dstQuad[2][1] * ssFactor,
              dstQuad[3][0] * ssFactor, dstQuad[3][1] * ssFactor
            ]);
            const M_ss = cv.getPerspectiveTransform(srcTri, dstTriSS);

            rugBgrToWarp = moirePreBlur(rugMat, this.srcQuad, dstQuad);
            const warpedRugSS = new cv.Mat();
            const rugMaskMat = cv.matFromArray(this.h_rug, this.w_rug, cv.CV_8UC1, Array.from(this.rugMaskSrc));
            const warpedMaskSS = new cv.Mat();

            cv.warpPerspective(rugBgrToWarp, warpedRugSS, M_ss, new cv.Size(this.w * ssFactor, this.h * ssFactor), cv.INTER_LINEAR);
            cv.warpPerspective(rugMaskMat, warpedMaskSS, M_ss, new cv.Size(this.w * ssFactor, this.h * ssFactor), cv.INTER_NEAREST);

            cv.resize(warpedRugSS, warpedRug, new cv.Size(this.w, this.h), 0, 0, cv.INTER_AREA);
            const warpedMaskSmooth = new cv.Mat();
            cv.resize(warpedMaskSS, warpedMaskSmooth, new cv.Size(this.w, this.h), 0, 0, cv.INTER_AREA);
            cv.threshold(warpedMaskSmooth, warpedMask, 127, 255, cv.THRESH_BINARY);

            dstTriSS.delete(); M_ss.delete(); warpedRugSS.delete(); rugMaskMat.delete(); warpedMaskSS.delete(); warpedMaskSmooth.delete();
          } else {
            const M = cv.getPerspectiveTransform(srcTri, dstTri);
            const rugMaskMat = cv.matFromArray(this.h_rug, this.w_rug, cv.CV_8UC1, Array.from(this.rugMaskSrc));
            cv.warpPerspective(rugMat, warpedRug, M, new cv.Size(this.w, this.h), cv.INTER_LINEAR);
            cv.warpPerspective(rugMaskMat, warpedMask, M, new cv.Size(this.w, this.h), cv.INTER_NEAREST);
            M.delete(); rugMaskMat.delete();
          }

          const floorMaskMat = cv.matFromArray(this.h, this.w, cv.CV_8UC1, Array.from(this.floorMaskU8));
          const tightFloor = new cv.Mat();
          const erodeKernel = cv.Mat.ones(3, 3, cv.CV_8UC1);
          cv.erode(floorMaskMat, tightFloor, erodeKernel);

          const intersection = new cv.Mat();
          cv.bitwise_and(warpedMask, tightFloor, intersection);

          // Calculate convex hull & moments for visible hull centroid (only on HQ frame or when needed)
          if (hq && floorOverlap > 0) {
            const contours = new cv.MatVector();
            const hierarchy = new cv.Mat();
            cv.findContours(intersection, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);
            if (contours.size() > 0) {
              let largestIdx = 0, largestArea = 0;
              for (let i = 0; i < contours.size(); i++) {
                const area = cv.contourArea(contours.get(i));
                if (area > largestArea) { largestArea = area; largestIdx = i; }
              }
              const hull = new cv.Mat();
              cv.convexHull(contours.get(largestIdx), hull);
              const Mm = cv.moments(hull);
              if (Mm.m00 > 0) {
                this.last_hull_cx = Mm.m10 / Mm.m00;
                this.last_hull_cy = Mm.m01 / Mm.m00;
              }
              hull.delete();
            }
            contours.delete(); hierarchy.delete();
          }

          // Apply contact & ambient floor shadows (createShadow) — skip during active drag (hq = false)
          const shadowMat = hq ? createShadow(dstQuad, this.h, this.w, floorMaskMat, warpedMask, hq) : null;
          const roomShadowedMat = roomMat.clone();
          if (shadowMat) {
            const shadowBgr = new cv.Mat();
            const shadowVector = new cv.MatVector();
            shadowVector.push_back(shadowMat); shadowVector.push_back(shadowMat); shadowVector.push_back(shadowMat); shadowVector.push_back(shadowMat);
            cv.merge(shadowVector, shadowBgr);
            const invShadow = new cv.Mat();
            const onesShadow = new cv.Mat(this.h, this.w, cv.CV_32FC4, new cv.Scalar(1.0, 1.0, 1.0, 1.0));
            cv.subtract(onesShadow, shadowBgr, invShadow);
            onesShadow.delete();
            roomShadowedMat.convertTo(roomShadowedMat, cv.CV_32FC4, 1.0 / 255.0);
            cv.multiply(roomShadowedMat, invShadow, roomShadowedMat);
            roomShadowedMat.convertTo(roomShadowedMat, cv.CV_8UC4, 255.0);

            shadowMat.delete(); shadowBgr.delete(); shadowVector.delete(); invShadow.delete();
          }

          // True Float Alpha Blending (1-to-1 match for soft_edges & rug_warper.py line 824)
          // final = roomShadowed * (1 - alpha) + warpedRug * alpha
          const roomFloat = new cv.Mat();
          roomShadowedMat.convertTo(roomFloat, cv.CV_32FC4, 1.0 / 255.0);

          const rugFloat = new cv.Mat();
          warpedRug.convertTo(rugFloat, cv.CV_32FC4, 1.0 / 255.0);

          // 1. Rug Quad Alpha (soft edge for open floor)
          const softRugAlpha = new cv.Mat();
          warpedMask.convertTo(softRugAlpha, cv.CV_32FC1, 1.0 / 255.0);
          const kSize = hq ? new cv.Size(13, 13) : new cv.Size(5, 5);
          cv.GaussianBlur(softRugAlpha, softRugAlpha, kSize, hq ? 2.0 : 1.0, hq ? 2.0 : 1.0);

          // 2. Floor Mask Occlusion Alpha (smooth anti-aliased edge for chair legs/furniture/walls)
          const floorFloatMat = new cv.Mat();
          floorMaskMat.convertTo(floorFloatMat, cv.CV_32FC1, 1.0 / 255.0);
          const tightFloorAlpha = new cv.Mat();
          cv.GaussianBlur(floorFloatMat, tightFloorAlpha, hq ? new cv.Size(7, 7) : new cv.Size(3, 3), hq ? 2.0 : 1.0, hq ? 2.0 : 1.0);

          // 3. Combined Alpha = softRugAlpha * tightFloorAlpha
          const softAlpha = new cv.Mat();
          cv.multiply(softRugAlpha, tightFloorAlpha, softAlpha);
          floorFloatMat.delete(); tightFloorAlpha.delete(); softRugAlpha.delete();

          const alpha4 = new cv.Mat();
          const vec = new cv.MatVector();
          vec.push_back(softAlpha); vec.push_back(softAlpha); vec.push_back(softAlpha); vec.push_back(softAlpha);
          cv.merge(vec, alpha4);
          vec.get(0).delete(); vec.delete();

          const rugBlended = new cv.Mat();
          cv.multiply(rugFloat, alpha4, rugBlended);

          const oneMinusAlpha = new cv.Mat();
          const onesMat = new cv.Mat(this.h, this.w, cv.CV_32FC4, new cv.Scalar(1.0, 1.0, 1.0, 1.0));
          cv.subtract(onesMat, alpha4, oneMinusAlpha);
          onesMat.delete();

          const roomBlended = new cv.Mat();
          cv.multiply(roomFloat, oneMinusAlpha, roomBlended);

          const finalFloat = new cv.Mat();
          cv.add(rugBlended, roomBlended, finalFloat);

          let finalMat = new cv.Mat();
          finalFloat.convertTo(finalMat, cv.CV_8UC4, 255.0);

          // Apply post-composite frequency-break edge blend (skip during active drag)
          if (hq) {
            const edgeBlendedMat = moireEdgeBlend(finalMat, warpedMask, roomMat, 0.09);
            if (edgeBlendedMat !== finalMat) {
              finalMat.delete();
              finalMat = edgeBlendedMat;
            }
          }

          cv.imshow(this.helper2DCanvas, finalMat);

          // Complete Garbage Collection of OpenCV Mats
          if (rugBgrToWarp && rugBgrToWarp !== rugMat) rugBgrToWarp.delete();
          roomMat.delete(); rugMat.delete(); srcTri.delete(); dstTri.delete();
          warpedRug.delete(); warpedMask.delete(); floorMaskMat.delete(); tightFloor.delete(); erodeKernel.delete();
          intersection.delete(); roomShadowedMat.delete();
          roomFloat.delete(); rugFloat.delete(); softAlpha.delete(); alpha4.delete();
          rugBlended.delete(); oneMinusAlpha.delete(); roomBlended.delete(); finalFloat.delete(); finalMat.delete();

          return this.helper2DCanvas;
        } catch (cvErr) {
          console.warn("OpenCV.js execution fallback to WebGL/Canvas path:", cvErr);
        }
      }

      // Render WebGL frame
      if (this.gl && this.glProgram) {
        try {
          const gl = this.gl;
          gl.viewport(0, 0, this.w, this.h);
          gl.useProgram(this.glProgram);

          const uHInv = gl.getUniformLocation(this.glProgram, 'u_H_inv');
          const uRes = gl.getUniformLocation(this.glProgram, 'u_resolution');
          const uRugSize = gl.getUniformLocation(this.glProgram, 'u_rugSize');

          gl.uniformMatrix3fv(uHInv, false, new Float32Array([
            H_inv[0], H_inv[3], H_inv[6],
            H_inv[1], H_inv[4], H_inv[7],
            H_inv[2], H_inv[5], H_inv[8]
          ]));
          gl.uniform2f(uRes, this.w, this.h);
          gl.uniform2f(uRugSize, this.w_rug, this.h_rug);

          gl.activeTexture(gl.TEXTURE0);
          gl.bindTexture(gl.TEXTURE_2D, this.texRoom);
          gl.uniform1i(gl.getUniformLocation(this.glProgram, 'u_roomTexture'), 0);

          gl.activeTexture(gl.TEXTURE1);
          gl.bindTexture(gl.TEXTURE_2D, this.texRug);
          gl.uniform1i(gl.getUniformLocation(this.glProgram, 'u_rugTexture'), 1);

          gl.activeTexture(gl.TEXTURE2);
          gl.bindTexture(gl.TEXTURE_2D, this.texFloor);
          gl.uniform1i(gl.getUniformLocation(this.glProgram, 'u_floorMaskTexture'), 2);

          gl.drawArrays(gl.TRIANGLES, 0, 6);

          this.helper2DCtx.drawImage(this.offscreenCanvas, 0, 0);
          return this.helper2DCanvas;
        } catch (glErr) {
          console.warn("WebGL render error fallback to 2D:", glErr);
        }
      }

      // Standard fallback 2D rendering path
      this.helper2DCtx.drawImage(this.roomCanvas, 0, 0);
      try {
        const hctx = this.helper2DCtx;
        hctx.save();
        hctx.beginPath();
        hctx.moveTo(dstQuad[0][0], dstQuad[0][1]);
        hctx.lineTo(dstQuad[1][0], dstQuad[1][1]);
        hctx.lineTo(dstQuad[2][0], dstQuad[2][1]);
        hctx.lineTo(dstQuad[3][0], dstQuad[3][1]);
        hctx.closePath();
        hctx.clip();

        const x0 = dstQuad[0][0], y0 = dstQuad[0][1];
        const x1 = dstQuad[1][0], y1 = dstQuad[1][1];
        const x3 = dstQuad[3][0], y3 = dstQuad[3][1];
        const a = (x1 - x0) / Math.max(1, this.w_rug);
        const b = (y1 - y0) / Math.max(1, this.w_rug);
        const c = (x3 - x0) / Math.max(1, this.h_rug);
        const d = (y3 - y0) / Math.max(1, this.h_rug);

        hctx.transform(a, b, c, d, x0, y0);
        hctx.drawImage(this.rugCanvas, 0, 0);
        hctx.restore();
      } catch (_) {}

      return this.helper2DCanvas;
    }

    render(cx, cy, w_ft, h_ft, rot_deg, hq) {
      const renderedCanvas = this._render_rgba(cx, cy, w_ft, h_ft, rot_deg, hq);
      const ctx = renderedCanvas.getContext('2d');
      return ctx.getImageData(0, 0, this.w, this.h);
    }

    render_canvas(cx, cy, w_ft, h_ft, rot_deg, hq) {
      return this._render_rgba(cx, cy, w_ft, h_ft, rot_deg, hq);
    }

    render_into_buffer(cx, cy, w_ft, h_ft, rot_deg, hq, out_buffer) {
      const imgData = this.render(cx, cy, w_ft, h_ft, rot_deg, hq);
      if (!imgData || !out_buffer) return false;
      out_buffer.set(imgData.data);
      return true;
    }

    cleanup() {
      if (this.gl) {
        try {
          if (this.texRoom) this.gl.deleteTexture(this.texRoom);
          if (this.texRug) this.gl.deleteTexture(this.texRug);
          if (this.texFloor) this.gl.deleteTexture(this.texFloor);
          if (this.glProgram) this.gl.deleteProgram(this.glProgram);
        } catch (_) {}
        this.gl = null;
      }
      this.roomCanvas = null;
      this.rugCanvas = null;
      this.polyCanvas = null;
      this.helper2DCanvas = null;
      this.offscreenCanvas = null;
      this.floorMaskU8 = null;
      this.floorMaskData = null;
    }
  }

  // --- EXPORTED API & PYODIDE SHIM ---
  let warperInstance = null;

  function initialize_warpers_strict(room, rug, mask, K, P) {
    try {
      if (!room || !rug || !mask) {
        console.error('❌ Missing room/rug/mask inputs to warper');
        return false;
      }
      if (warperInstance) {
        warperInstance.cleanup();
      }
      warperInstance = new RugWarperEngine(room, rug, mask, K, P);

      if (typeof window !== 'undefined' && typeof window.onWarperReady === 'function') {
        window.onWarperReady();
      }
      console.log('✅ Rug Warper Engine Ready (Full OpenCV & WebGL Parity Engine)');
      return true;
    } catch (e) {
      console.error('❌ Warper Engine Init Exception:', e);
      return false;
    }
  }

  function render_frame_3d(cx, cy, w, h, r, hq) {
    if (!warperInstance) return null;
    return warperInstance.render(cx, cy, w, h, r, hq);
  }

  function render_frame_canvas(cx, cy, w, h, r, hq) {
    if (!warperInstance) return null;
    return warperInstance.render_canvas(cx, cy, w, h, r, hq);
  }

  function render_frame_into_buffer(cx, cy, w, h, r, hq, out_buffer) {
    if (!warperInstance) return false;
    return warperInstance.render_into_buffer(cx, cy, w, h, r, hq, out_buffer);
  }

  function get_last_reject_reason() {
    return warperInstance ? warperInstance.last_reject : null;
  }

  function get_visible_ratio_js() {
    return warperInstance ? warperInstance.last_visible_ratio : 0.0;
  }

  function get_floor_centroid_js() {
    if (!warperInstance || !warperInstance.floorMaskU8) return null;
    let sumX = 0, sumY = 0, count = 0;
    const u8 = warperInstance.floorMaskU8;
    const w = warperInstance.w;
    const h = warperInstance.h;
    const step = 4;
    for (let y = 0; y < h; y += step) {
      for (let x = 0; x < w; x += step) {
        if (u8[y * w + x] > 10) {
          sumX += x;
          sumY += y;
          count++;
        }
      }
    }
    if (count === 0) return null;
    return [sumX / count, sumY / count];
  }

  function get_projected_corners_js(cx_px, cy_px, w_ft, h_ft, rot_deg) {
    if (!warperInstance) return null;
    const w_m = parseFloat(w_ft) * 0.3048;
    const h_m = parseFloat(h_m_ft(h_ft)) * 0.3048;
    const [pts] = warperInstance.geo.get_projected_corners(
      parseFloat(cx_px), parseFloat(cy_px), w_m, h_m, parseFloat(rot_deg)
    );
    return pts;
  }

  function h_m_ft(v) { return v; }

  function get_visible_hull_centroid_js() {
    if (!warperInstance) return null;
    if (warperInstance.last_hull_cx < 0 || warperInstance.last_hull_cy < 0) return null;
    return [warperInstance.last_hull_cx, warperInstance.last_hull_cy];
  }

  function get_persistent_anchor_js() {
    if (!warperInstance) return null;
    if (warperInstance._persistent_anchor_cx === null) return null;
    return [warperInstance._persistent_anchor_cx, warperInstance._persistent_anchor_cy];
  }

  function set_persistent_anchor_js(cx, cy) {
    if (!warperInstance) return false;
    warperInstance._persistent_anchor_cx = parseFloat(cx);
    warperInstance._persistent_anchor_cy = parseFloat(cy);
    return true;
  }

  function cleanup_warper_strict() {
    if (warperInstance) {
      warperInstance.cleanup();
      warperInstance = null;
    }
    console.log('✅ Warper memory and WebGL textures cleared.');
    return true;
  }

  // Bind exported functions to window
  window.initialize_warpers_strict = initialize_warpers_strict;
  window.initialize_warpers_js = initialize_warpers_strict;
  window.render_frame_3d = render_frame_3d;
  window.render_frame_3d_js = render_frame_3d;
  window.render_frame_canvas = render_frame_canvas;
  window.render_frame_canvas_js = render_frame_canvas;
  window.render_frame_into_buffer = render_frame_into_buffer;
  window.render_frame_into_buffer_js = render_frame_into_buffer;
  window.get_last_reject_reason = get_last_reject_reason;
  window.get_last_reject_reason_js = get_last_reject_reason;
  window.get_visible_ratio_js = get_visible_ratio_js;
  window.get_floor_centroid_js = get_floor_centroid_js;
  window.get_projected_corners_js = get_projected_corners_js;
  window.get_visible_hull_centroid_js = get_visible_hull_centroid_js;
  window.get_persistent_anchor_js = get_persistent_anchor_js;
  window.set_persistent_anchor_js = set_persistent_anchor_js;
  window.cleanup_warper_strict = cleanup_warper_strict;

  window.pyodide = {
    globals: {
      get: function(name) { return window[name] || null; },
      set: function(name, val) { window[name] = val; }
    },
    toPy: function(val) { return val; },
    runPython: function() {}
  };

  window.isPyodideReady = true;
  window.isWarperReady = true;

})(typeof window !== 'undefined' ? window : this);
